import React, { useState } from "react";
import axios from "axios";
import SEO from "./SEO";
import "./Contact.css";

/**
 * Contact page — MAILCHIMP-CONTACT-FORM-001 frontend visual restyle + payload
 * field consumption.
 *
 * Authoritative inputs:
 *   - docs/design/MAILCHIMP-CONTACT-FORM-001-design.md (Zuri visual spec)
 *   - docs/backend/MAILCHIMP-CONTACT-FORM-001-tech.md  (Simone contract snapshot)
 *
 * Backward-compat posture: response field shape is purely additive on the
 * backend per Simone's contract §2. This component still works against the
 * pre-Sprint-3 deployed backend (which only returns `message`); the new
 * fields (`audience`, `customerConfirmation`, `teamNotification`, `contactId`)
 * gracefully degrade when missing.
 *
 * Per AC Scenario 5: Mailchimp outages do NOT 500 the request — failures
 * surface in payload fields. The frontend's friendly canned banner copy is
 * unchanged on partial failure; we do NOT show "Mailchimp is down" to users.
 * Only Contact.save() failures (5xx) trigger the error banner.
 */
const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // Banner state: { kind: "success" | "error", message: string, sub?: string }.
  // sub is the optional secondary line (e.g., "Confirmation email sent").
  const [banner, setBanner] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear field error as soon as the user types something valid.
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  // Inline-validation copy per design spec §2.6.
  const validateField = (name, value) => {
    const trimmed = (value || "").trim();
    switch (name) {
      case "name":
        return trimmed ? null : "Tell us your name.";
      case "email":
        if (!trimmed) return "We need an email to reply to you.";
        // Light regex — server is the source of truth on validation.
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
          ? null
          : "Enter a valid email address.";
      case "message":
        return trimmed ? null : "Add a message so we know what's up.";
      default:
        return null;
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setFieldErrors((prev) => {
      if (!error) {
        if (!prev[name]) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      }
      return { ...prev, [name]: error };
    });
  };

  const validateAll = () => {
    const errors = {};
    ["name", "email", "message"].forEach((name) => {
      const err = validateField(name, formData[name]);
      if (err) errors[name] = err;
    });
    return errors;
  };

  // Auto-dismiss success banner after 8s (design spec §2.6 success state).
  React.useEffect(() => {
    if (!banner || banner.kind !== "success") return undefined;
    const t = setTimeout(() => setBanner(null), 8000);
    return () => clearTimeout(t);
  }, [banner]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const errors = validateAll();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setBanner(null);
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/contact`,
        formData
      );
      // Per Simone's contract §3, success returns 200 with at least
      // `message`. New additive fields gracefully default when missing
      // (older deployed backend, integration-test stubs, etc.).
      const payload = response?.data || {};
      const customerConfirmationSent =
        payload?.customerConfirmation?.sent === true;
      // Frontend uses canned copy per design spec — backend message is for
      // logging, not user display.
      setBanner({
        kind: "success",
        message: "Thanks - we'll be in touch within 1 business day.",
        sub: customerConfirmationSent
          ? "Confirmation email sent - check your inbox."
          : null,
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
      setFieldErrors({});
    } catch (err) {
      // Error banner persists until next submit attempt (design spec §2.6).
      // Form data is preserved so the user can retry without retyping.
      // eslint-disable-next-line no-console
      // (no console.log per CLAUDE.md — Sentry will pick this up via
      // the existing axios interceptor / global error handler.)
      setBanner({
        kind: "error",
        message:
          "We couldn't send your message. Try again, or email us directly at info@divitiadora.com.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <SEO
        title="Contact Diviti Adora | Wellness Center Tucson | Oro Valley Location"
        description="Contact Diviti Adora Wellness Studio in Oro Valley. Reach out to us for appointments, questions about our infrared sauna, red light therapy, chromotherapy, and halotherapy services."
        keywords="contact Diviti Adora, wellness center Tucson Arizona, Oro Valley wellness, infrared sauna Oro Valley AZ, red light therapy near Oro Valley, wellness studio location, 1930 E Tangerine Rd Oro Valley, holistic healing Oro Valley, alternative wellness center Tucson, health and wellness studio Marana"
        canonicalUrl="/contact"
      />
      <div className="contact-card">
        <h1 className="contact-heading">Contact Us</h1>
        <span className="contact-heading-accent" aria-hidden="true" />
        <p className="contact-intro">
          We&apos;d love to hear from you. We typically respond within 1
          business day.
        </p>

        {banner && banner.kind === "success" && (
          <div
            className="contact-banner contact-banner--success"
            role="status"
            aria-live="polite"
            data-testid="contact-banner-success"
          >
            <span className="contact-banner__icon" aria-hidden="true">
              {/* check mark drawn in CSS — see Contact.css */}
            </span>
            <div className="contact-banner__body">
              <p className="contact-banner__message">{banner.message}</p>
              {banner.sub && (
                <p className="contact-banner__sub">{banner.sub}</p>
              )}
            </div>
          </div>
        )}

        {banner && banner.kind === "error" && (
          <div
            className="contact-banner contact-banner--error"
            role="alert"
            aria-live="assertive"
            data-testid="contact-banner-error"
          >
            <span className="contact-banner__icon" aria-hidden="true" />
            <div className="contact-banner__body">
              <p className="contact-banner__message">
                We couldn&apos;t send your message. Try again, or email us
                directly at{" "}
                <a
                  href="mailto:info@divitiadora.com"
                  className="contact-banner__link"
                >
                  info@divitiadora.com
                </a>
                .
              </p>
            </div>
          </div>
        )}

        <form
          className="contact-form"
          onSubmit={handleSubmit}
          noValidate
          aria-busy={loading || undefined}
        >
          <div className="contact-field">
            <label className="contact-label" htmlFor="name">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className={`contact-input ${
                fieldErrors.name ? "contact-input--error" : ""
              }`}
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              aria-invalid={fieldErrors.name ? true : undefined}
              aria-describedby={fieldErrors.name ? "name-error" : undefined}
            />
            {fieldErrors.name && (
              <p
                id="name-error"
                className="contact-field-error"
                role="alert"
              >
                {fieldErrors.name}
              </p>
            )}
          </div>

          <div className="contact-field">
            <label className="contact-label" htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={`contact-input ${
                fieldErrors.email ? "contact-input--error" : ""
              }`}
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              aria-invalid={fieldErrors.email ? true : undefined}
              aria-describedby={
                fieldErrors.email ? "email-error" : undefined
              }
            />
            {fieldErrors.email && (
              <p
                id="email-error"
                className="contact-field-error"
                role="alert"
              >
                {fieldErrors.email}
              </p>
            )}
          </div>

          <div className="contact-field">
            <label className="contact-label" htmlFor="phone">
              Phone (optional)
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              className="contact-input"
              value={formData.phone}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          <div className="contact-field">
            <label className="contact-label" htmlFor="message">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              rows="5"
              className={`contact-input contact-textarea ${
                fieldErrors.message ? "contact-input--error" : ""
              }`}
              value={formData.message}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loading}
              aria-invalid={fieldErrors.message ? true : undefined}
              aria-describedby={
                fieldErrors.message ? "message-error" : undefined
              }
            ></textarea>
            {fieldErrors.message && (
              <p
                id="message-error"
                className="contact-field-error"
                role="alert"
              >
                {fieldErrors.message}
              </p>
            )}
          </div>

          <div className="contact-submit-row">
            <button
              type="submit"
              className="contact-submit"
              disabled={loading}
              aria-label={loading ? "Sending message" : "Send Message"}
              data-testid="contact-submit"
            >
              {loading ? (
                <span
                  className="contact-submit__spinner"
                  aria-hidden="true"
                />
              ) : (
                "Send Message"
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="contact-card contact-location-card">
        <h2 className="contact-location-heading">Find us</h2>
        <span className="contact-heading-accent contact-heading-accent--sm" aria-hidden="true" />
        <p className="contact-phone">
          <span className="contact-phone__icon" aria-hidden="true" />
          <a className="contact-phone__link" href="tel:5202227031">
            520.222.7031
          </a>
        </p>
        <p className="contact-address">
          <span className="contact-pin__icon" aria-hidden="true" />
          1930 E Tangerine Rd #100, Oro Valley, AZ 85737
        </p>
        <div className="contact-map-frame">
          <iframe
            title="Studio Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2831.9092613691455!2d-110.94321418485079!3d32.42647964806032!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x86d60dc96fcfedf3%3A0xf8271d485c1bd5bd!2s1930%20E%20Tangerine%20Rd%20%23100%2C%20Oro%20Valley%2C%20AZ%2085737!5e0!3m2!1sen!2sus!4v1739725053829!5m2!1sen!2sus"
            width="600"
            height="450"
            style={{ border: 0 }}
            allowFullScreen={true}
            loading="lazy"
          ></iframe>
        </div>
      </div>
    </div>
  );
};

export default Contact;
